/**
 * What a candidate is told when they leave the test.
 *
 * In the middle of the screen, over everything, pulsing red. It used to sit
 * low on the page as a grey strip, and candidates read straight past it — the
 * one message saying the next slip ends their test looked like a note about
 * the recording. Nothing else can be done until it is acknowledged, which is
 * the point.
 *
 * Its own component so the screens gallery shows exactly what a candidate
 * sees, rather than a copy that drifts.
 */
export function BreachWarning({
  count,
  limit,
  onAcknowledge,
  contained = false,
}: {
  count: number;
  limit: number;
  onAcknowledge: () => void;
  /** Inside a box rather than over the whole window: the gallery's sample. */
  contained?: boolean;
}) {
  const last = limit - count <= 1;
  return (
    <div className={`exam__breach${contained ? ' exam__breach--contained' : ''}`}>
      <div
        className="exam__breach__card"
        role="alertdialog"
        aria-modal={!contained}
        aria-labelledby="breach-title"
        aria-describedby="breach-text"
      >
        <svg className="exam__breach__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2.5 1.5 21h21L12 2.5Z" fill="currentColor" />
          <path d="M12 9v5.5" stroke="var(--x-bg)" strokeWidth="2.4" strokeLinecap="round" />
          <circle cx="12" cy="17.6" r="1.35" fill="var(--x-bg)" />
        </svg>
        <h2 id="breach-title" className="exam__breach__title">
          Warning {count} of {limit}
        </h2>
        <p id="breach-text" className="exam__breach__text">
          You left your test. This has been recorded as a breach of exam conditions.
        </p>
        <p className="exam__breach__next">
          {last
            ? 'If you leave your test again, it will end and your centre will be notified.'
            : `You may be warned ${limit - count} more times before your test ends.`}
        </p>
        <button
          className="xbtn exam__breach__ok"
          // Focused, so Enter answers it and a keyboard user is not left
          // tabbing through a paper they cannot reach.
          autoFocus={!contained}
          onClick={onAcknowledge}
        >
          I understand
        </button>
      </div>
    </div>
  );
}
